/**
 * 이상한 방 - 매치의 원래 방 문 (정원 1 토큰 · 줄 · 차례 예약)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 4.4절(원래 방 - 정원 1, 체류 상한, 재입장 쿨타임)
 *            `이상한방-상태모델-v0.1.md` 11.3절(입장 허가는 서버가 쥔다) · 로드맵 모델 2.6절(원래 방 정원 1)
 *
 * ## 문은 하나, 타이머는 사람마다
 *
 * 4.4절의 "정원 1"은 **문 토큰**(`door.holder`)의 문제다. 반면 체류 상한과 재입장 쿨타임은
 * 사람마다 따로 흐른다 - 그 산술은 싱글과 같은 리프 `originVisit.ts`가 하고, 이 파일은 그 위에
 * 토큰·줄·예약을 얹는다. 이 파일에 "쿨타임이 몇 초인가"나 "체류가 몇 초인가"를 묻는 비교식이 없는
 * 이유가 그것이다. 여기 있는 비교는 "문이 비었는가"·"내 차례인가"뿐이다.
 *
 * ## 자동 워프는 없다
 *
 * 문이 비면 줄 머리를 `reservedFor`로 세우고 `doorYourTurn{graceMs}`를 보낸다. **들어가는 것은
 * 그 사람이 `requestOrigin`을 다시 눌러야 일어난다.** 보고 있던 곳에서 갑자기 원래 방으로 끌려가면
 * 관찰이 끊기고, 그 순간 원래 방은 참조가 아니라 방해가 된다. 유예를 넘기면 줄에서 빠지고 다음 사람이다.
 *
 * ## 쿨타임 중에는 줄에도 못 선다
 *
 * 줄이 쿨타임을 우회하면 "나가자마자 줄에 서서 쿨타임이 끝나는 순간 들어간다"가 되고, 그건 곧
 * 원본↔변경본 고속 토글(깜빡비교 = 정답 자동 추출)이다. 쿨타임이 유일한 방어선이므로(`tuning.ts`
 * `originCooldownSeconds` 주석) 줄도 그 뒤에 선다.
 *
 * ## 퇴장 경로는 하나다 - 줄에서 빠지는 경로도 하나다
 *
 * 수동(`leaveOrigin`) · 체류 초과(`tick`) · 관전 전환(잔탄·탄창 바닥) · 라운드 종료가 전부 `releaseDoor`를
 * 지난다. 경로가 갈리면 어느 하나에서만 줄 머리가 예약을 못 받고, 그 사람은 영원히 기다린다.
 * 줄도 같다 - 스스로 빠지는 것(`leaveQueue`)과 관전 전환이 `dropFromQueue` 하나를 지난다. 관전자가 줄이나
 * 예약에 남으면 문이 그 사람에게 5초 예약되고, 관전자는 어떤 이벤트도 못 보내므로 아무도 못 들어간다.
 *
 * ## 입장 판정은 `classifyOriginRequest` 하나다
 *
 * `onRequestOrigin`(실제 판정)과 `canEnterOrigin`(버튼의 얼굴)이 같은 조건 사슬을 각자 적으면 한쪽만 바뀐 날
 * 버튼은 켜져 있는데 눌러도 안 들어간다. 그래서 순서는 분류 함수 하나에만 있고 둘은 그 결과로 분기한다.
 */

import { MS_PER_SECOND } from '../../core/time'
import type { PlayerId } from '../../core/types'
import {
  canStartOriginVisit,
  enterOriginVisit,
  exitOriginVisit,
  originStayRemainingMs,
  tickOriginVisit,
} from '../originVisit'
import type { Tuning } from '../tuning'
import { idle, patchPlayer, playerOf, seatsInOrder } from './matchSelectors'
import type { MatchEffect, MatchState, MatchStep, OriginDoor } from './matchTypes'

/* --- 상수 ------------------------------------------------------------------------ */

/** 아무도 없는 문. 라운드 시작과 수색 진입·종료가 이 값을 편다. */
export const EMPTY_DOOR: OriginDoor = Object.freeze({
  holder: null,
  queue: [],
  reservedFor: null,
  reservedRemainingMs: 0,
})

/** 줄 자리는 1부터 센다 - "3번째"를 HUD가 그대로 쓴다. */
const FIRST_QUEUE_POSITION = 1

/* --- 효과 ------------------------------------------------------------------------ */

/** 전원에게 가는 문 상태. 보유자의 체류 잔여를 같이 싣는다 - 줄 선 사람이 "몇 초 남았나"를 본다. */
export function doorChangedEffect(state: MatchState, tuning: Tuning): MatchEffect {
  const holder = state.door.holder === null ? undefined : playerOf(state, state.door.holder)
  return {
    to: 'all',
    body: {
      type: 'doorChanged',
      door: state.door,
      holderRemainingMs: holder === undefined ? 0 : originStayRemainingMs(holder.origin, tuning),
    },
  }
}

/** 예약 유예 전체(ms). 뷰(`matchView.doorView`)가 게이지의 분모로 같은 값을 싣는다 - 예약할 때와 그릴 때가 같은 함수여야 한다. */
export function graceMs(tuning: Tuning): number {
  return tuning.doorReserveSeconds * MS_PER_SECOND
}

/* --- 토큰 전이 -------------------------------------------------------------------- */

/**
 * 줄 머리에게 차례를 준다. 줄이 비었으면 예약 없는 빈 문이다.
 * `doorYourTurn`은 머리에게만 간다 - 남들에게는 `doorChanged`로 충분하다.
 */
function reserveNext(state: MatchState, tuning: Tuning): MatchStep {
  const head = state.door.queue[0]
  if (head === undefined) {
    return { state: { ...state, door: { ...EMPTY_DOOR, queue: [] } }, effects: [] }
  }
  const door: OriginDoor = {
    holder: null,
    queue: state.door.queue,
    reservedFor: head,
    reservedRemainingMs: graceMs(tuning),
  }
  return {
    state: { ...state, door },
    effects: [{ to: head, body: { type: 'doorYourTurn', graceMs: door.reservedRemainingMs } }],
  }
}

/**
 * 보유자가 문을 비운다. 좌석의 방문 상태는 호출자가 이미 `exitOriginVisit`으로 바꿔 두었다 -
 * 이 함수는 **토큰만** 다룬다. 이유는 관전 전환이다: 관전으로 넘어가는 사람의 좌석은 `spectating`과 함께
 * 한 번에 바뀌어야 하고, 그 갱신은 `matchShoot`의 것이다.
 */
export function releaseDoor(state: MatchState, tuning: Tuning): MatchStep {
  if (state.door.holder === null) return idle(state)
  const released: MatchState = { ...state, door: { ...state.door, holder: null } }
  const reserved = reserveNext(released, tuning)
  return {
    state: reserved.state,
    effects: [...reserved.effects, doorChangedEffect(reserved.state, tuning)],
  }
}

/** 방문 상태를 퇴장으로 돌리고 문을 비우고 그 사람에게 워프·소리를 낸다. 수동·자동 퇴장이 함께 쓴다. */
function exitOrigin(state: MatchState, id: PlayerId, tuning: Tuning): MatchStep {
  const player = playerOf(state, id)
  if (player === undefined) return idle(state)
  const left = patchPlayer(state, id, { origin: exitOriginVisit(player.origin, tuning) })
  const released = releaseDoor(left, tuning)
  return {
    state: released.state,
    effects: [
      { to: id, body: { type: 'warpTo', origin: false } },
      { to: id, body: { type: 'syncProxies' } },
      { to: id, body: { type: 'playSound', id: 'warp-out' } },
      ...released.effects,
    ],
  }
}

/* --- 게이트 ---------------------------------------------------------------------- */

/** 문이 이 사람에게 열려 있는가 - 보유자가 없고, 예약이 없거나 내 것이다. `classifyOriginRequest`의 4번 조건이다. */
function doorOpenFor(door: OriginDoor, id: PlayerId): boolean {
  return door.holder === null && (door.reservedFor === null || door.reservedFor === id)
}

/**
 * `requestOrigin`을 누르면 무슨 일이 일어나는가. **순서가 규칙이고 이 함수에만 있다**(머리말):
 *  1. `ignore` - 수색 밖 · 없는 사람 · 관전자 · 이미 안 → 무시(참조 동일). 관전자는 어떤 이벤트로도 상태를 못 바꾼다(4.3절)
 *  2. `cooldown` - 쿨타임. **줄에도 못 선다**(머리말)
 *  3. `enter` - 문이 비었고 예약이 없거나 내 것 → 입장
 *  4. `queue` - 그 밖(보유자 있음 · 남의 예약) → 줄
 *
 * M11에서 `role`(체인저는 못 들어간다)이 빠졌다 - 역할이 사라져 좌석 전부가 찾는 사람이고, 전원이 자기 구획을
 * 고쳤으므로 **원래 방은 누구에게나 남의 구획을 대조하는 곳**이다.
 */
export type OriginRequestOutcome = 'ignore' | 'cooldown' | 'enter' | 'queue'

export function classifyOriginRequest(state: MatchState, id: PlayerId): OriginRequestOutcome {
  if (state.phase !== 'hunt') return 'ignore'
  const player = playerOf(state, id)
  if (player === undefined || player.spectating || player.origin.isInOriginRoom) return 'ignore'
  // `canStartOriginVisit`은 "밖 + 쿨타임 0"이다. 안에 있는 경우는 위에서 걸렀으니 여기서 거짓이면 쿨타임이다.
  if (!canStartOriginVisit(player.origin)) return 'cooldown'
  return doorOpenFor(state.door, id) ? 'enter' : 'queue'
}

/**
 * 지금 `requestOrigin`을 누르면 **실제로 들어가는가.** `matchView.canEnterOrigin`이 이것이다 - 클라이언트가
 * 셀렉터를 부르지 않으므로 버튼의 얼굴은 이 불리언 하나에서 나온다. 줄서기(`queue`)는 "들어간다"가 아니라 거짓이다.
 */
export function canEnterOrigin(state: MatchState, id: PlayerId): boolean {
  return classifyOriginRequest(state, id) === 'enter'
}

/* --- 이벤트 ---------------------------------------------------------------------- */

function denied(
  state: MatchState,
  id: PlayerId,
  reason: 'cooldown' | 'occupied',
  remainingMs: number,
  queuePosition: number | null,
  extra: readonly MatchEffect[] = [],
): MatchStep {
  return {
    state,
    effects: [
      { to: id, body: { type: 'originEntryDenied', reason, remainingMs, queuePosition } },
      { to: id, body: { type: 'playSound', id: 'shot-blocked' } },
      ...extra,
    ],
  }
}

/**
 * "들어가겠다." 입장 · 줄서기 · 예약 수락이 전부 이 하나다. 판정은 `classifyOriginRequest`가 했고 여기는 그 결과대로
 * 상태를 바꾸고 알린다 - `enter`면 `warpTo`는 나에게만, `queue`면 줄에 붙고 `occupied` 거부에 내 자리를 싣는다.
 */
export function onRequestOrigin(state: MatchState, id: PlayerId, tuning: Tuning): MatchStep {
  const outcome = classifyOriginRequest(state, id)
  if (outcome === 'ignore') return idle(state)
  const player = playerOf(state, id)
  if (player === undefined) return idle(state)
  if (outcome === 'cooldown') return denied(state, id, 'cooldown', player.origin.originCooldownMs, null)

  const { door } = state
  if (outcome === 'enter') {
    const entered: MatchState = {
      ...patchPlayer(state, id, { origin: enterOriginVisit(player.origin) }),
      door: {
        holder: id,
        queue: door.queue.filter((waiting) => waiting !== id),
        reservedFor: null,
        reservedRemainingMs: 0,
      },
    }
    return {
      state: entered,
      effects: [
        { to: id, body: { type: 'warpTo', origin: true } },
        { to: id, body: { type: 'playSound', id: 'warp-in' } },
        doorChangedEffect(entered, tuning),
      ],
    }
  }

  const alreadyAt = door.queue.indexOf(id)
  if (alreadyAt >= 0) {
    // 이미 줄에 있다. 상태는 그대로(참조 동일)이고 자리만 다시 알려 준다 - 버튼을 두 번 누른 사람에게 침묵은 고장이다.
    return denied(state, id, 'occupied', 0, alreadyAt + FIRST_QUEUE_POSITION)
  }

  const queued: MatchState = { ...state, door: { ...door, queue: [...door.queue, id] } }
  return denied(queued, id, 'occupied', 0, queued.door.queue.length, [
    doorChangedEffect(queued, tuning),
  ])
}

/** 스스로 나온다. 보유자가 아니면 무시 - 밖에서 "나가기"는 아무 일도 아니다. */
export function onLeaveOrigin(state: MatchState, id: PlayerId, tuning: Tuning): MatchStep {
  if (state.phase !== 'hunt' || state.door.holder !== id) return idle(state)
  return exitOrigin(state, id, tuning)
}

/**
 * 줄에서 빠진다. 내 차례가 예약돼 있었다면 다음 사람에게 넘긴다. 줄에 없으면 참조 동일.
 * `leaveQueue`(스스로)와 관전 전환(`matchShoot.becomeSpectator`)이 **같은 함수**를 쓴다(머리말).
 */
export function dropFromQueue(state: MatchState, id: PlayerId, tuning: Tuning): MatchStep {
  if (!state.door.queue.includes(id)) return idle(state)
  const queue = state.door.queue.filter((waiting) => waiting !== id)
  const wasReserved = state.door.reservedFor === id
  const shortened: MatchState = {
    ...state,
    door: wasReserved
      ? { holder: state.door.holder, queue, reservedFor: null, reservedRemainingMs: 0 }
      : { ...state.door, queue },
  }
  const next = wasReserved && shortened.door.holder === null ? reserveNext(shortened, tuning) : idle(shortened)
  return { state: next.state, effects: [...next.effects, doorChangedEffect(next.state, tuning)] }
}

/** 스스로 줄에서 빠진다. */
export function onLeaveQueue(state: MatchState, id: PlayerId, tuning: Tuning): MatchStep {
  return dropFromQueue(state, id, tuning)
}

/* --- 틱 -------------------------------------------------------------------------- */

/**
 * 예약 유예를 흘린다. 0에 닿으면 머리를 줄에서 빼고 다음 사람에게 넘긴다.
 * 초과분을 다음 사람의 유예에서 깎지 않는다 - 그래야 한 구간 안에서 `tick(a); tick(b) ≡ tick(a + b)`다.
 */
function tickReservation(state: MatchState, dtMs: number, tuning: Tuning): MatchStep {
  const { door } = state
  if (door.reservedFor === null) return idle(state)

  const remaining = Math.max(0, door.reservedRemainingMs - dtMs)
  if (remaining > 0) {
    return { state: { ...state, door: { ...door, reservedRemainingMs: remaining } }, effects: [] }
  }

  const dropped: MatchState = {
    ...state,
    door: {
      holder: door.holder,
      queue: door.queue.filter((waiting) => waiting !== door.reservedFor),
      reservedFor: null,
      reservedRemainingMs: 0,
    },
  }
  const next = reserveNext(dropped, tuning)
  return { state: next.state, effects: [...next.effects, doorChangedEffect(next.state, tuning)] }
}

/**
 * 수색 중 매 틱. 예약 유예를 먼저 흘리고, 그 다음 술래 전원의 체류·쿨타임을 흘린다(리프). 체류 초과는
 * **수동 퇴장과 같은 함수**로 내보낸다. 변화가 없으면 입력 참조를 그대로 돌려준다.
 *
 * **순서가 병합 불변을 지킨다.** 체류 초과로 이 틱에 막 생긴 예약을 같은 틱의 dt로 깎으면,
 * `tick(a); tick(b)`는 유예가 `G - b`인데 `tick(a + b)`는 `G - a - b`가 된다. 예약을 먼저 흘리면
 * 새 예약은 다음 틱부터 흐르고 두 경로가 같은 값을 만든다(`originVisit.ts`의 "한 구간 안에서"와 같은 논리).
 */
export function tickOrigin(state: MatchState, dtMs: number, tuning: Tuning): MatchStep {
  const reservation = tickReservation(state, dtMs, tuning)
  let next = reservation.state
  const effects: MatchEffect[] = [...reservation.effects]

  // 좌석 전부다 - M11에서 역할이 사라져 "술래만"이라는 걸러 냄이 없어졌다(관전자는 리프가 참조 동일을 돌려준다).
  for (const player of seatsInOrder(next)) {
    const ticked = tickOriginVisit(player.origin, dtMs, tuning)
    if (ticked.exited) {
      const exited = exitOrigin(next, player.id, tuning)
      next = exited.state
      effects.push(...exited.effects)
    } else if (ticked.visit !== player.origin) {
      next = patchPlayer(next, player.id, { origin: ticked.visit })
      // 쿨타임이 이 틱에 0에 닿았다. 시계만 흐른 틱은 `step`이 없으므로(`matchHost.commit`) 이 한 줄이 없으면 그 사람의
      // 버튼은 다음 무관한 걸음까지 잠긴 얼굴이다. 한 번만 난다 - 다음 틱부터는 `tickOriginVisit`이 참조 동일을 돌려준다.
      if (player.origin.originCooldownMs > 0 && ticked.visit.originCooldownMs <= 0) {
        effects.push({ to: player.id, body: { type: 'originReady' } })
      }
    }
  }

  return { state: next, effects }
}
